import { Check, Crown, Sparkles, Zap } from 'lucide-react';
import { useSnapshot } from 'valtio';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { aiUpgradeProxy, aiUpgradeActions } from '@/state/ai-upgrade-state';
import { AI_PLANS } from '@polybank/shared';
import type { AIFeature, AIPlanType } from '@polybank/shared';

// Human-readable feature labels
const FEATURE_LABELS: Record<AIFeature, string> = {
  'generate-rule': 'AI Rule Builder',
  'bulk-generate-rules': 'Bulk Rule Generation',
  'bulk-categorize': 'Bulk Categorization',
  'hybrid-categorize': 'Smart Categorization',
  'generate-query': 'AI Transaction Search',
};

// Display order for features in plan cards
const FEATURE_ORDER: AIFeature[] = [
  'generate-rule',
  'bulk-generate-rules',
  'bulk-categorize',
  'hybrid-categorize',
  'generate-query',
];

function getCurrentMonthName(): string {
  return new Date().toLocaleString('default', { month: 'long' });
}

interface PlanCardProps {
  planKey: AIPlanType;
  hitFeature: AIFeature | null;
  currentPlan: AIPlanType;
  onUpgrade: (plan: AIPlanType) => void;
}

function PlanCard({
  planKey,
  hitFeature,
  currentPlan,
  onUpgrade,
}: PlanCardProps) {
  const plan = AI_PLANS[planKey];
  const isCurrent = planKey === currentPlan;
  const isSmart = planKey === 'smart';
  const isPro = planKey === 'pro';

  return (
    <Card
      className={`flex flex-col ${isSmart ? 'ring-2 ring-purple-400' : ''} ${isCurrent ? 'ring-2 ring-slate-400 bg-slate-50' : ''}`}
    >
      <CardContent className='flex flex-col flex-1 p-4 gap-3'>
        {/* Plan name + badge */}
        <div className='space-y-1.5'>
          <div className='flex items-center gap-2 flex-wrap'>
            <span className='font-semibold'>{plan.name}</span>
            {isCurrent && (
              <span className='text-xs bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full font-medium'>
                ✓ Your Plan
              </span>
            )}
            {isSmart && (
              <span className='text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium'>
                ⭐ Most Popular
              </span>
            )}
            {isPro && (
              <span className='text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1'>
                <Crown className='w-3 h-3' />
                Pro
              </span>
            )}
          </div>
        </div>

        {/* Feature limits */}
        <ul className='space-y-1.5 flex-1'>
          {FEATURE_ORDER.map((feature) => {
            const { monthlyItemLimit } = plan.features[feature];
            const isHit = feature === hitFeature && isCurrent;
            return (
              <li
                key={feature}
                className={`text-sm flex items-center justify-between gap-3 rounded px-2 py-1.5 ${
                  isHit
                    ? 'bg-red-50 text-red-700 font-semibold'
                    : 'text-muted-foreground'
                }`}
              >
                <span>{FEATURE_LABELS[feature]}</span>
                <span className='shrink-0 font-medium'>
                  {monthlyItemLimit === null ? '∞' : `${monthlyItemLimit}/mo`}
                </span>
              </li>
            );
          })}
        </ul>

        {/* CTA */}
        {isCurrent ? (
          <Button variant='outline' disabled className='w-full mt-auto'>
            <Check className='w-4 h-4 mr-1.5' />
            Current Plan
          </Button>
        ) : isSmart ? (
          <Button
            className='w-full mt-auto bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white border-0'
            onClick={() => onUpgrade(planKey)}
          >
            <Zap className='w-4 h-4 mr-1.5' />
            Upgrade to Smart →
          </Button>
        ) : (
          <Button
            variant='outline'
            className='w-full mt-auto'
            onClick={() => onUpgrade(planKey)}
          >
            Upgrade to Pro →
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export function AIUpgradeModal() {
  const { open, feature, plan } = useSnapshot(aiUpgradeProxy);

  const featureLabel = feature ? FEATURE_LABELS[feature] : 'AI feature';
  const featureLimit = feature
    ? AI_PLANS[plan].features[feature].monthlyItemLimit
    : null;
  const monthName = getCurrentMonthName();

  const handleUpgrade = (_targetPlan: AIPlanType) => {
    toast.info('Coming soon! Upgrade plans will be available shortly.');
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) aiUpgradeActions.hide();
      }}
    >
      <DialogContent className='sm:max-w-[900px] max-h-[90vh] overflow-y-auto p-0 gap-0'>
        {/* Hero gradient band */}
        <div className='bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-8 rounded-t-lg'>
          <div className='flex items-center gap-3'>
            <div className='w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center'>
              <Sparkles className='w-5 h-5 text-white' />
            </div>
            <DialogHeader>
              <DialogTitle className='text-white text-xl'>
                You&apos;ve reached your AI limit
              </DialogTitle>
            </DialogHeader>
          </div>
          <p className='text-white/90 text-sm mt-2 ml-[52px]'>
            You used your {featureLimit !== null ? featureLimit : ''}{' '}
            {featureLabel} this {monthName}
          </p>

          {/* Progress bar — full / red */}
          {featureLimit !== null && (
            <div className='ml-[52px] mt-3 space-y-1'>
              <div className='h-2 bg-white/30 rounded-full overflow-hidden'>
                <div className='h-full bg-red-400 rounded-full w-full' />
              </div>
              <p className='text-white/80 text-xs'>
                {featureLimit}/{featureLimit} used
              </p>
            </div>
          )}
        </div>

        {/* Plan cards */}
        <div className='p-6 space-y-4'>
          <p className='text-sm text-muted-foreground font-medium'>
            Upgrade your plan to get more AI features
          </p>

          <div className='grid grid-cols-1 sm:grid-cols-3 gap-3'>
            {(['free', 'smart', 'pro'] as AIPlanType[]).map((planKey) => (
              <PlanCard
                key={planKey}
                planKey={planKey}
                hitFeature={feature}
                currentPlan={plan}
                onUpgrade={handleUpgrade}
              />
            ))}
          </div>

          {/* Maybe later */}
          <div className='flex justify-center pt-2'>
            <Button
              variant='ghost'
              onClick={aiUpgradeActions.hide}
              className='text-muted-foreground'
            >
              Maybe later
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
